/*
 * Search and filters over the CMMS tool register.
 *
 * Pure functions of their arguments so the rules can be tested without rendering a grid, in the
 * shape of cmmsSpareParts/sparePartSearch.js. The matching rule itself is shared — see
 * features/cmms/cmmsSearch.js for why every CMMS search box has to agree about what
 * "spindle 12" means.
 *
 * Client-side, like the register it filters: /api/tools hands over every row and
 * /api/tool-monitoring/status every reading, so this narrows something we are already holding.
 */
import { filterBySearchText } from "../cmms/cmmsSearch";
import { isToolServiceAlerting } from "../cmms/toolServiceStatus";

/*
 * The row's searchable text.
 *
 * The schedule title is in here and is the reason this file exists rather than a one-line
 * filterBySearchText call on name alone. It is not a field of the tool — it arrives on the
 * status reading joined onto the row — and it is what someone types when they are looking for
 * every tool on "500h service" rather than for one tool by name.
 *
 * The worst reading's title only, not every schedule's. Matching a title the grid does not show
 * would leave a row on screen with no visible reason for being there, which reads as a broken
 * filter. The drawer is where the other schedules are named.
 *
 * The level label is deliberately absent. "Overdue" is a filter, not a search term, and it has
 * its own switch below — typing it would otherwise half-work in one language and not at all in
 * the other.
 */
export const getToolSearchText = (row) =>
  [row?.name, row?.description, row?.status?.scheduleTitle]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const filterToolsBySearch = (rows, query) =>
  filterBySearchText(rows, query, getToolSearchText);

/*
 * The register narrowed by the two switches and the search box.
 *
 * `watchedOnly` asks a different question from `attentionOnly`, which is why they are two
 * switches and not one three-way control: the first is "does anything measure this tool", the
 * second is "is it saying something". A tool with a healthy schedule answers yes to one and no
 * to the other, and both are things someone opens this page to see.
 *
 * Attention implies watched — an unwatched tool has no level to alert on — so the two are
 * applied in sequence rather than as alternatives.
 *
 * Level comes off the status the server resolved, through isToolServiceAlerting. No `>= 80`
 * here: each schedule carries its own WarnAtPercent, and a threshold guessed in the client
 * disagrees with the server silently, in the reassuring direction.
 */
export const filterTools = (rows, { search, watchedOnly, attentionOnly } = {}) => {
  let out = rows ?? [];

  if (watchedOnly) out = out.filter((row) => Boolean(row.status));
  if (attentionOnly) out = out.filter((row) => isToolServiceAlerting(row.status?.level));

  return filterToolsBySearch(out, search);
};
